import type { Asset } from '@schibsted-svp/svp-api-types';
import type { Player } from '@schibsted-svp/web-player';
import type { ResolvedPermissionValue } from '@vgtv/consent-manager';

import type { AdnSiteConfig, DeviceType, XandrKeywords } from '../types';
import { prefixExternalKeywords } from '../prepareKeywords';
import { getCategoryShortPath } from '../../../utils/player/category';

export type AdsOption = {
  keywords?: Record<string, string | number | boolean>;
  sectionKeywords?: string[];
};

export type GlobalKeywordsBuilder = (
  keywords: Partial<
    Record<
      | 'countryCode'
      | 'supplyType'
      | 'publisherName'
      | 'pageType'
      | 'publisherGroup'
      | 'newsSection',
      string | string[]
    >
  >,
  asset: Asset,
  deviceType: DeviceType,
  cmpConsent: ResolvedPermissionValue,
  dynamicAR: boolean,
  player: Player,
  siteConfig?: AdnSiteConfig<'ab' | 'svd'>
) => Promise<Partial<XandrKeywords>>;

// Common param shape shared by the classic ADN path (`getRequestKeywords`)
// and the VMAP path (`getVmapKeywords`) for the Aftonbladet/SvD brands.
export type RequestKeywordsParams = {
  player: Player;
  asset: Asset;
  deviceType: DeviceType;
  countryCode?: string;
  supplyType?: string;
  dynamicAR: boolean;
  cmpConsent: ResolvedPermissionValue;
  siteConfig: AdnSiteConfig<'ab' | 'svd'>;
  ads?: AdsOption;
  isLoggedIn: boolean;
  pulseUserId?: string | null;
};

// Shared request-level keyword orchestration for the Aftonbladet/SvD
// brands: assembles the `newsSection` keyword, builds the global keyword
// group via the supplied `buildGlobalKeywords`, and merges it with the
// ad-slot and login keywords. Callers apply the final `kw_` prefixing
// (`getRequestKeywords` via `prepareXandrKeywords`).
export async function buildRequestKeywords(
  params: RequestKeywordsParams & { buildGlobalKeywords: GlobalKeywordsBuilder }
): Promise<Partial<XandrKeywords>> {
  const {
    player,
    asset,
    deviceType,
    countryCode,
    supplyType,
    dynamicAR,
    cmpConsent,
    siteConfig,
    ads,
    isLoggedIn,
    pulseUserId,
    buildGlobalKeywords,
  } = params;

  const categoryShortPath = asset
    ? getCategoryShortPath(asset.category)
    : undefined;
  const newsSection = [
    siteConfig.mainSection,
    ...(ads?.sectionKeywords ?? []),
    categoryShortPath,
  ]
    .filter((v): v is string => Boolean(v))
    .join(',');

  const global = await buildGlobalKeywords(
    {
      countryCode,
      supplyType,
      publisherName: siteConfig.keywords.publisherName as string,
      pageType: siteConfig.keywords.pageType as string,
      publisherGroup: siteConfig.keywords.publisherGroup as string,
      newsSection,
    },
    asset,
    deviceType,
    cmpConsent,
    dynamicAR,
    player,
    siteConfig
  );

  const adKeywords = {
    ...prefixExternalKeywords(ads?.keywords ?? {}),
    'kw_aa-sch-has_context_keyword': ads?.keywords?.['aa-sch-context_keyword']
      ? '1'
      : '0',
    'kw_aa-sch-externalid_src': Number(!!pulseUserId) + Number(!!isLoggedIn),
  };

  return {
    ...global,
    ...adKeywords,
    'kw_se-generic-loggedin': isLoggedIn ? 'yes' : 'no',
  };
}
